(() => {
  const form = document.getElementById('AndersHeaderSearchForm');
  const input = document.getElementById('AndersHeaderSearch');
  const panel = document.getElementById('AndersHeaderSearchSuggestions');
  if (!form || !input || !panel) return;

  const resultsEl = panel.querySelector('[data-anders-live-results]');
  const allLink = panel.querySelector('[data-anders-live-all]');
  const predictiveUrl = panel.dataset.predictiveUrl || '/search/suggest';
  const searchUrl = panel.dataset.searchUrl || '/search';
  let timer = null;
  let controller = null;
  let requestId = 0;
  let catalogPromise = null;

  const close = () => {
    panel.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    resultsEl.replaceChildren();
    allLink.hidden = true;
  };

  const open = () => {
    panel.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  };

  const searchHref = (query) => {
    const params = new URLSearchParams();
    params.set('q', query.trim());
    params.set('type', 'product');
    params.set('options[prefix]', 'last');
    return `${searchUrl}?${params.toString()}`;
  };

  const isInternalNumberQuery = (query) => /^\d{4,12}$/.test(query.trim());

  const imageUrlFor = (product) => {
    if (product?.featured_image?.url) return product.featured_image.url;
    if (typeof product?.featured_image === 'string') return product.featured_image;
    if (product?.image) return product.image;
    return '';
  };

  const normalizeCatalogProduct = (product) => ({
    title: product?.title || '',
    url: product?.url || '#',
    body: product?.body || '',
    image: product?.image || '',
    featured_image: product?.image || '',
  });

  const fetchCatalogPage = async (page) => {
    const root = window.Shopify?.routes?.root || '/';
    const cacheBust = Math.floor(Date.now() / 60000);
    const url = `${root}collections/all?view=anders-search-index&page=${page}&_=${cacheBust}`;
    const response = await fetch(url, {
      headers: { Accept: 'application/json, text/plain, */*' },
      cache: 'no-store',
    });
    if (!response.ok) throw new Error(`Catalog request failed: ${response.status}`);
    const text = await response.text();
    return JSON.parse(text);
  };

  const loadCurrentCatalog = async () => {
    if (catalogPromise) return catalogPromise;

    catalogPromise = (async () => {
      const first = await fetchCatalogPage(1);
      const totalPages = Math.min(Math.max(Number(first?.pages) || 1, 1), 20);
      const products = Array.isArray(first?.products) ? [...first.products] : [];

      if (totalPages > 1) {
        const remaining = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, index) => fetchCatalogPage(index + 2))
        );
        remaining.forEach((payload) => {
          if (Array.isArray(payload?.products)) products.push(...payload.products);
        });
      }

      return products.map(normalizeCatalogProduct);
    })().catch((error) => {
      catalogPromise = null;
      throw error;
    });

    return catalogPromise;
  };

  const exactInternalNumberMatches = async (query) => {
    const cleanQuery = query.trim();
    const products = await loadCurrentCatalog();
    const escaped = cleanQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const exactNumber = new RegExp(`(^|\\D)${escaped}(?=\\D|$)`);

    return products.filter((product) => {
      const haystack = `${product.title || ''}\n${product.body || ''}`;
      return exactNumber.test(haystack);
    });
  };

  const render = (products, query, options = {}) => {
    const matchingProducts = (Array.isArray(products) ? products : []).slice(0, 6);

    resultsEl.replaceChildren();

    if (!matchingProducts.length) {
      const empty = document.createElement('p');
      empty.className = 'anders-live-search__empty';
      empty.textContent = `Keine passenden Produkte für „${query.trim()}“ gefunden.`;
      resultsEl.appendChild(empty);
      allLink.hidden = true;
      open();
      return;
    }

    matchingProducts.forEach((product, index) => {
      const link = document.createElement('a');
      link.className = 'anders-live-search__item';
      link.href = product.url;
      link.setAttribute('role', 'option');
      link.id = `AndersLiveSearchOption-${index + 1}`;

      const imageWrap = document.createElement('span');
      imageWrap.className = 'anders-live-search__image-wrap';
      const imageUrl = imageUrlFor(product);
      if (imageUrl) {
        const img = document.createElement('img');
        img.className = 'anders-live-search__image';
        img.src = imageUrl;
        img.alt = '';
        img.loading = 'lazy';
        imageWrap.appendChild(img);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'anders-live-search__image-placeholder';
        placeholder.textContent = '□';
        imageWrap.appendChild(placeholder);
      }

      const title = document.createElement('p');
      title.className = 'anders-live-search__title';
      title.textContent = product.title;

      const view = document.createElement('span');
      view.className = 'anders-live-search__view';
      view.textContent = 'ANSEHEN →';

      link.append(imageWrap, title, view);
      resultsEl.appendChild(link);
    });

    if (options.exactInternalNumber) {
      allLink.hidden = true;
    } else {
      allLink.href = searchHref(query);
      allLink.hidden = false;
    }
    open();
  };

  const showLoading = (text = 'Passende Produkte werden gesucht …') => {
    resultsEl.replaceChildren();
    const loading = document.createElement('p');
    loading.className = 'anders-live-search__loading';
    loading.textContent = text;
    resultsEl.appendChild(loading);
    allLink.hidden = true;
    open();
  };

  const load = async (query) => {
    const cleanQuery = query.trim();
    if (cleanQuery.length < 2) {
      close();
      return;
    }

    if (controller) controller.abort();
    controller = new AbortController();
    const currentRequest = ++requestId;

    if (isInternalNumberQuery(cleanQuery)) {
      showLoading('Interne Nummer wird geprüft …');
      try {
        const products = await exactInternalNumberMatches(cleanQuery);
        if (currentRequest !== requestId) return;
        render(products, cleanQuery, { exactInternalNumber: true });
      } catch (error) {
        if (currentRequest !== requestId) return;
        // If the exact catalog endpoint is temporarily unavailable, fall back to Shopify predictive search.
        await loadPredictive(cleanQuery, currentRequest);
      }
      return;
    }

    showLoading();
    await loadPredictive(cleanQuery, currentRequest);
  };

  const loadPredictive = async (cleanQuery, currentRequest) => {
    const params = new URLSearchParams();
    params.set('q', cleanQuery);
    params.set('resources[type]', 'product');
    params.set('resources[limit]', '10');
    params.set('resources[options][fields]', 'title,body');
    params.set('resources[options][unavailable_products]', 'last');

    const endpoint = `${predictiveUrl.replace(/\/$/, '')}.json?${params.toString()}`;

    try {
      const response = await fetch(endpoint, {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Search request failed: ${response.status}`);
      const data = await response.json();
      if (currentRequest !== requestId) return;
      const products = data?.resources?.results?.products || [];
      render(products, cleanQuery);
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (currentRequest !== requestId) return;
      resultsEl.replaceChildren();
      const fallback = document.createElement('p');
      fallback.className = 'anders-live-search__empty';
      fallback.textContent = 'Die Live-Suche ist gerade nicht verfügbar. Bitte verwenden Sie „Suchen“.';
      resultsEl.appendChild(fallback);
      allLink.href = searchHref(cleanQuery);
      allLink.hidden = false;
      open();
    }
  };

  input.addEventListener('input', () => {
    window.clearTimeout(timer);
    const query = input.value;
    if (query.trim().length < 2) {
      close();
      return;
    }
    timer = window.setTimeout(() => load(query), 180);
  });

  input.addEventListener('focus', () => {
    if (input.value.trim().length >= 2) load(input.value);
  });

  input.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close();
  });

  form.addEventListener('submit', async (event) => {
    const query = input.value.trim();
    if (!query) {
      event.preventDefault();
      close();
      return;
    }

    input.value = query;

    if (!isInternalNumberQuery(query)) return;

    event.preventDefault();
    showLoading('Interne Nummer wird geprüft …');
    try {
      const matches = await exactInternalNumberMatches(query);
      if (matches.length > 0 && matches[0]?.url) {
        window.location.assign(matches[0].url);
        return;
      }
    } catch (error) {
      // Fall through to the native search URL below.
    }
    window.location.assign(searchHref(query));
  });

  document.addEventListener('click', (event) => {
    if (!panel.contains(event.target) && !form.contains(event.target)) close();
  });
})();
